import { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_OF_WEEK = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function PremiumDateRangePicker({ startDate, endDate, onChange }) {
  const [activePicker, setActivePicker] = useState(null); // 'start' | 'end' | null
  const [currentViewDate, setCurrentViewDate] = useState(new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setActivePicker(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Select date...";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getDaysInMonth = () => {
    const year = currentViewDate.getFullYear();
    const month = currentViewDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDayIndex; i++) {
      days.push({ day: null, currentMonth: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      days.push({ 
        day: i, 
        dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`, 
        currentMonth: true 
      });
    }
    return days;
  };

  const handleDateSelect = (dateStr) => {
    if (activePicker === "start") {
      onChange("start_date", dateStr);
    } else if (activePicker === "end") {
      onChange("end_date", dateStr);
    }
    setActivePicker(null);
  };

  const changeMonth = (direction) => {
    setCurrentViewDate(new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() + direction, 1));
  };

  return (
    <div className="grid grid-cols-2 gap-3 relative" ref={containerRef}>
      {/* Start Date Control */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-bold tracking-wide text-slate-400 uppercase">Start Date</label>
        <button
          type="button"
          onClick={() => setActivePicker(activePicker === "start" ? null : "start")}
          className={`w-full h-9 bg-white border rounded-xl px-3 text-xs font-semibold flex items-center justify-between shadow-3xs transition outline-none ${
            activePicker === "start" 
              ? "border-[#002855] ring-4 ring-[#002855]/5 text-[#002855]" 
              : "border-slate-200 text-slate-800 hover:border-slate-300"
          }`}
        >
          <span className={startDate ? "text-slate-800" : "text-slate-400 font-normal"}>
            {formatDisplayDate(startDate)}
          </span>
          <CalendarIcon size={14} className="text-slate-400" />
        </button>
      </div>

      {/* End Date Control */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-bold tracking-wide text-slate-400 uppercase">End Date</label>
        <button
          type="button"
          onClick={() => setActivePicker(activePicker === "end" ? null : "end")}
          className={`w-full h-9 bg-white border rounded-xl px-3 text-xs font-semibold flex items-center justify-between shadow-3xs transition outline-none ${
            activePicker === "end" 
              ? "border-[#002855] ring-4 ring-[#002855]/5 text-[#002855]" 
              : "border-slate-200 text-slate-800 hover:border-slate-300"
          }`}
        >
          <span className={endDate ? "text-slate-800" : "text-slate-400 font-normal"}>
            {formatDisplayDate(endDate)}
          </span>
          <CalendarIcon size={14} className="text-slate-400" />
        </button>
      </div>

      {/* Popover Calendar Grid Panel */}
      {activePicker && (
        <div className={`absolute top-[64px] bg-white border border-slate-200 rounded-2xl shadow-xl p-3 z-50 w-[260px] animate-dropdown-fade ${
          activePicker === "end" ? "right-0" : "left-0"
        }`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-black text-slate-900 tracking-tight">
              {MONTHS[currentViewDate.getMonth()]} {currentViewDate.getFullYear()}
            </span>
            <div className="flex gap-1">
              <button type="button" onClick={() => changeMonth(-1)} className="w-6 h-6 border border-slate-100 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"><ChevronLeft size={14} /></button>
              <button type="button" onClick={() => changeMonth(1)} className="w-6 h-6 border border-slate-100 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"><ChevronRight size={14} /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 text-center mb-1">
            {DAYS_OF_WEEK.map((day) => (
              <span key={day} className="text-[10px] font-bold text-slate-400/80 uppercase tracking-wider h-5 flex items-center justify-center">{day}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 text-center gap-0.5">
            {getDaysInMonth().map((item, idx) => {
              const isSelected = item.dateStr && (startDate === item.dateStr || endDate === item.dateStr);
              return (
                <div key={idx} className="h-7 w-7 flex items-center justify-center">
                  {item.day ? (
                    <button
                      type="button"
                      onClick={() => handleDateSelect(item.dateStr)}
                      className={`h-6 w-6 rounded-lg text-[11px] font-bold flex items-center justify-center transition-all ${
                        isSelected ? "bg-[#002855] text-white shadow-2xs" : "text-slate-700 hover:bg-slate-100/80"
                      }`}
                    >
                      {item.day}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}